import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import { RFQList, NewRFQ } from "@/pages/RFQs";
import RFQDetail from "@/pages/RFQDetail";
import { QuoteList, QuoteDetail, ReaderQuoteDetail } from "@/pages/Quotes";
import Config from "@/pages/Config";
import Users from "@/pages/Users";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import Billing from "@/pages/Billing";
import PdfDiagnostics from "@/pages/PdfDiagnostics";
import PdfVerification from "@/pages/PdfVerification";
import ExpertQuote from "@/pages/ExpertQuote";
import ShopCalibration from "@/pages/ShopCalibration";
import CalibrationCase from "@/pages/CalibrationCase";
import P05Pilot from "@/pages/P05Pilot";
import SpikeP1 from "@/pages/SpikeP1";
import VerificationQueue from "@/pages/VerificationQueue";
import PriceLists from "@/pages/PriceLists";
import MachinePark from "@/pages/MachinePark";
import ReaderQueue from "@/pages/ReaderQueue";
import ReaderReview from "@/pages/ReaderReview";
import HardenedResultReview from "@/pages/HardenedResultReview";
import ReaderSupport from "@/pages/ReaderSupport";
import Beta03Delivery from "@/pages/Beta03Delivery";
import CollaudoLettore from "@/pages/CollaudoLettore";
import KnowledgeAI from "@/pages/KnowledgeAI";
import ExpertCaseEditor from "@/pages/ExpertCaseEditor";
import OperationalArchive from "@/pages/OperationalArchive";
import NewQuote from "@/pages/NewQuote";
import ImportRFQExcel from "@/pages/ImportRFQExcel";
import OperationalReaderQueue from "@/pages/OperationalReaderQueue";
import OperationalQuotes from "@/pages/OperationalQuotes";
import QuoteWorkspace from "@/pages/QuoteWorkspace";

// Anteprima grafica isolata «Precisione industriale»: caricata solo sulla sua rotta, stili scopati a .pi-preview.
const PrecisionePreview = React.lazy(() => import("@/preview/precisione/PrecisionePreview"));

const LEGACY_LABS = process.env.REACT_APP_ENABLE_LEGACY_LABS === "true";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/preview/precisione-industriale/*" element={<ProtectedRoute><React.Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm">Caricamento anteprima…</div>}><PrecisionePreview /></React.Suspense></ProtectedRoute>} />
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/operations/archive" element={<ProtectedRoute minRole="admin"><OperationalArchive /></ProtectedRoute>} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/rfqs" element={<RFQList />} />
            <Route path="/rfqs/new" element={<NewQuote />} />
            <Route path="/rfqs/import" element={<ImportRFQExcel />} />
            <Route path="/rfqs/:id" element={<RFQDetail />} />
            <Route path="/quotes" element={<OperationalQuotes />} />
            <Route path="/quote-preparation/:jobId" element={<QuoteWorkspace />} />
            <Route path="/quotes/reader/:jobId" element={<ReaderQuoteDetail />} />
            <Route path="/quotes/:id" element={<QuoteDetail />} />
            <Route path="/config" element={<Config />} />
            <Route path="/reader-queue" element={<OperationalReaderQueue />} />
            <Route path="/rfqs/:rfqId/reader-review/:jobId" element={<ReaderReview />} />
            <Route path="/operations/reader-support/:jobId" element={<ProtectedRoute minRole="admin"><ReaderSupport /></ProtectedRoute>} />
            <Route path="/reader-result/:jobId" element={<ProtectedRoute minRole="admin"><HardenedResultReview /></ProtectedRoute>} />
            <Route path="/beta03" element={<ProtectedRoute minRole="admin"><Beta03Delivery /></ProtectedRoute>} />
            <Route path="/collaudo-lettore" element={<ProtectedRoute minRole="admin"><CollaudoLettore /></ProtectedRoute>} />
            <Route path="/knowledge" element={<ProtectedRoute minRole="admin"><KnowledgeAI /></ProtectedRoute>} />
            <Route path="/knowledge/case/:caseId" element={<ProtectedRoute minRole="admin"><ExpertCaseEditor /></ProtectedRoute>} />
            <Route path="/price-lists" element={<PriceLists />} />
            <Route path="/machines" element={<MachinePark />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/users" element={<ProtectedRoute minRole="admin"><Users /></ProtectedRoute>} />
            <Route path="/audit" element={<ProtectedRoute minRole="admin"><Audit /></ProtectedRoute>} />
            {LEGACY_LABS && (<>
              <Route path="/pdf-jobs/:jobId/diagnostics" element={<ProtectedRoute minRole="admin"><PdfDiagnostics /></ProtectedRoute>} />
              <Route path="/pdf-jobs/:jobId/verify" element={<PdfVerification />} />
              <Route path="/pdf-jobs/:jobId/expert" element={<ExpertQuote />} />
              <Route path="/calibration" element={<ShopCalibration />} />
              <Route path="/calibration/:caseId" element={<CalibrationCase />} />
              <Route path="/p05" element={<P05Pilot />} />
              <Route path="/spike-p1" element={<SpikeP1 />} />
              <Route path="/verification-queue" element={<VerificationQueue />} />
            </>)}
            <Route path="/settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

